/**
 * Pure, framework-free helpers for the tenant credits page. Kept out of the
 * views so the balance-health logic — which drives the whole card's semantic
 * tone — is unit-tested rather than inline and silent. CREDITS ONLY: no money
 * ever enters these functions.
 */

import type {
  ICreditTransactionView,
  ICreditWalletExpiredLotView,
  ICreditWalletLotView,
  TCreditTransactionKind,
} from "@lexicanon/shared"

/**
 * Semantic health of the wallet, mapped to a Mantine semantic palette downstream.
 * `pilot` is the neutral tone used while enforcement is off — the balance is
 * shown, but it is not empty, low, or healthy in the charged sense.
 */
export type TBalanceTone = "healthy" | "low" | "empty" | "pilot"

/**
 * Below this many spendable credits we flag the wallet as "low" (warning tone).
 * 500 credits = €5 at the locked 100-credits-per-euro peg — a deliberately soft
 * line that nudges a top-up well before the balance actually runs dry.
 */
export const LOW_BALANCE_CREDITS = 500

/** A projected run-out within this many days also trips the "low" tone. */
export const RUNOUT_SOON_DAYS = 7

const DAY_MS = 86_400_000

/**
 * Derive the wallet's display tone from its balance and projected run-out.
 *   - `empty`   — balance at or below zero (danger).
 *   - `low`     — balance under {@link LOW_BALANCE_CREDITS}, OR projected to hit
 *                 zero within {@link RUNOUT_SOON_DAYS} (warning).
 *   - `healthy` — everything else (brand).
 * `now` is injectable for deterministic tests.
 *
 * While enforcement is off the tone is always `pilot`, including a zero or
 * negative balance: nothing is being charged, so "empty" would be a lie.
 */
export function deriveBalanceTone(
  balanceCredits: number,
  projectedZeroAt: string | null,
  now: Date = new Date(),
  enforcementEnabled = true,
): TBalanceTone {
  if (!enforcementEnabled) return "pilot"
  if (!Number.isFinite(balanceCredits) || balanceCredits <= 0) return "empty"
  if (balanceCredits < LOW_BALANCE_CREDITS) return "low"
  if (projectedZeroAt) {
    const zeroMs = new Date(projectedZeroAt).getTime()
    if (Number.isFinite(zeroMs)) {
      const days = (zeroMs - now.getTime()) / DAY_MS
      if (days <= RUNOUT_SOON_DAYS) return "low"
    }
  }
  return "healthy"
}

/** Map a balance tone to its Mantine semantic palette name (never a literal hue). */
export function toneColor(tone: TBalanceTone): "brand" | "warning" | "danger" | "info" {
  switch (tone) {
    case "healthy":
      return "brand"
    case "low":
      return "warning"
    case "empty":
      return "danger"
    case "pilot":
      return "info"
  }
}

/**
 * Ledger-kind → Mantine semantic palette for the kind pill. Inflows are
 * positive/neutral tones; expiry is a warning; adjustments are informational.
 */
export function kindColor(kind: TCreditTransactionKind): "success" | "brand" | "warning" | "info" {
  switch (kind) {
    case "grant":
      return "brand"
    case "purchase":
      return "success"
    case "refund":
      return "success"
    case "expiry":
      return "warning"
    case "adjustment":
      return "info"
  }
}

/** Credits from live lots lapsing within this many days read as "expiring soon" (warning). */
export const EXPIRY_SOON_DAYS = 14

/** Look-back window for the honest "lost to expiry recently" line. */
export const FORFEIT_WINDOW_DAYS = 90

/** Semantic tone for a spend category — drives the stacked bar + legend swatch. */
export type TCategoryTone = "brand" | "info" | "warning" | "neutral"

/** Semantic tone for a wallet lot chip — grants read brand, prepaid packs read info. */
export type TLotTone = "brand" | "info"

/** A plain-language runway projection derived from {@link ICreditWalletView.projectedZeroAt}. */
export interface IPace {
  /** Whole weeks of runway remaining (rounded, never below zero). */
  weeks: number
  /** ISO-8601 date the balance is projected to reach zero. */
  runOutAt: string
}

/**
 * Turn a projected run-out timestamp into a plain-language runway — whole weeks
 * left plus the run-out date — for the "At this pace" tile. Returns `null` when
 * there is no meaningful projection (no steady usage yet), the timestamp is
 * unparseable, or the run-out is already in the past. `now` is injectable.
 */
export function derivePace(projectedZeroAt: string | null, now: Date = new Date()): IPace | null {
  if (!projectedZeroAt) return null
  const zeroMs = new Date(projectedZeroAt).getTime()
  if (!Number.isFinite(zeroMs)) return null
  const diffMs = zeroMs - now.getTime()
  if (diffMs <= 0) return null
  return { weeks: Math.max(0, Math.round(diffMs / (7 * DAY_MS))), runOutAt: projectedZeroAt }
}

/** The nearest upcoming lot expiry and the credits riding on it, for the "Expiring soon" tile. */
export interface IExpiring {
  /** Remaining credits across every live lot sharing the earliest future expiry. */
  credits: number
  /** The expiry instant, ISO-8601. */
  expiresAt: string
  /** The lot kind sharing that expiry — drives the friendly source label. */
  kind: TCreditTransactionKind
  /** Whether the expiry falls within {@link EXPIRY_SOON_DAYS} (warning tone). */
  soon: boolean
}

/**
 * Find the nearest FUTURE lot expiry and sum the remaining credits of every live
 * lot sharing that earliest expiry, so a split grant reads as a single number.
 * Returns `null` when no live lot has a future expiry. `soon` trips inside
 * {@link EXPIRY_SOON_DAYS}. `now` is injectable for deterministic tests.
 */
export function deriveExpiringSoon(liveLots: ICreditWalletLotView[], now: Date = new Date()): IExpiring | null {
  const nowMs = now.getTime()
  let earliestMs = Number.POSITIVE_INFINITY
  for (const lot of liveLots) {
    if (!lot.expiresAt) continue
    const ms = new Date(lot.expiresAt).getTime()
    if (!Number.isFinite(ms) || ms <= nowMs) continue
    if (ms < earliestMs) earliestMs = ms
  }
  if (!Number.isFinite(earliestMs)) return null

  let credits = 0
  let kind: TCreditTransactionKind | null = null
  let expiresAt = ""
  for (const lot of liveLots) {
    if (!lot.expiresAt) continue
    if (new Date(lot.expiresAt).getTime() !== earliestMs) continue
    credits += lot.remainingCredits
    kind ??= lot.kind
    expiresAt = lot.expiresAt
  }
  if (kind === null) return null

  return { credits, expiresAt, kind, soon: earliestMs - nowMs <= EXPIRY_SOON_DAYS * DAY_MS }
}

/**
 * Sum credits forfeited to lot expiry within the trailing `windowDays`. Lots
 * lacking an expiry date are counted best-effort (they still lost their unspent
 * credit). `now` is injectable. Returns 0 when nothing lapsed — the caller omits
 * the line entirely rather than showing a zero.
 */
export function sumForfeitedRecently(
  expiredLots: ICreditWalletExpiredLotView[],
  now: Date = new Date(),
  windowDays: number = FORFEIT_WINDOW_DAYS,
): number {
  const cutoffMs = now.getTime() - windowDays * DAY_MS
  let total = 0
  for (const lot of expiredLots) {
    const ms = lot.expiresAt ? new Date(lot.expiresAt).getTime() : Number.NaN
    if (!Number.isFinite(ms) || ms >= cutoffMs) total += lot.forfeitedCredits
  }
  return total
}

/** A top-N split of spend rows plus the aggregated remainder. */
export interface IMeetingSplit<T> {
  /** The heaviest-spending rows, descending, capped at `topN`. */
  top: T[]
  /** Every row past the top-N, still ranked, so the tail can expand in place. */
  rest: T[]
  /** Combined credits of every row beyond the top-N. */
  restCredits: number
  /** How many rows the remainder aggregates. */
  restCount: number
}

/**
 * Rank spend rows by credits (descending) and split off the top `topN`,
 * aggregating the tail into a single "N more meetings…" figure. Pure — copies
 * before sorting so the caller's array is untouched.
 */
export function splitTopMeetings<T extends { credits: number }>(rows: readonly T[], topN = 5): IMeetingSplit<T> {
  const sorted = [...rows].sort((a, b) => b.credits - a.credits)
  const rest = sorted.slice(topN)
  return {
    top: sorted.slice(0, topN),
    rest,
    restCredits: rest.reduce((sum, row) => sum + row.credits, 0),
    restCount: rest.length,
  }
}

/** Map a spend category to its semantic tone (transcription = brand, summaries = info, images = warning). */
export function categoryTone(category: string): TCategoryTone {
  switch (category) {
    case "transcription":
      return "brand"
    case "llm":
      return "info"
    case "image":
      return "warning"
    default:
      return "neutral"
  }
}

/** Map a wallet lot to its chip tone — prepaid packs read info, grants read brand. */
export function lotTone(kind: TCreditTransactionKind): TLotTone {
  return kind === "purchase" ? "info" : "brand"
}

/**
 * Whether a lot's expiry falls within {@link EXPIRY_SOON_DAYS} of `now` (and is
 * not already past). Drives the warning sub-line on the wallet card. A `null`
 * expiry (never-expiring lot) is never "soon". `now` is injectable.
 */
export function isExpiringSoon(expiresAt: string | null, now: Date = new Date()): boolean {
  if (!expiresAt) return false
  const ms = new Date(expiresAt).getTime()
  if (!Number.isFinite(ms)) return false
  const diffMs = ms - now.getTime()
  return diffMs > 0 && diffMs <= EXPIRY_SOON_DAYS * DAY_MS
}

/** Clamp `value / total` to a 0–100 percentage; 0 when the total is non-positive. */
export function toPercent(value: number, total: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return 0
  return Math.max(0, Math.min(100, (value / total) * 100))
}

/**
 * Fewer than this many calendar days of usage is not enough to project a month.
 * A single day (the 1st, or a one-day window) says "too early" instead.
 */
export const MIN_PACE_ELAPSED_DAYS = 2

/** A full-month credit projection from this period's spend, plus an optional run-out date. */
export interface ISpendPace {
  /** Credits this pace would use across the whole current month, rounded. */
  projectedCredits: number
  /** Last local calendar day of `now`'s month. */
  monthEnd: Date
  /**
   * ISO run-out instant. Set only when enforcement is on, the balance is
   * positive, and the projection is still in the future.
   */
  runOutAt: string | null
}

/** Categories that actually consumed credits this period. Zeros stay off the legend. */
export function visibleCategories<T extends { credits: number }>(rows: readonly T[]): T[] {
  return rows.filter((row) => row.credits > 0)
}

/** Parse a local `YYYY-MM-DD`. Returns null for anything that is not that calendar day. */
function parseLocalYmd(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2]) - 1
  const day = Number(match[3])
  const date = new Date(year, month, day)
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null
  return date
}

/** Inclusive local calendar days from `from` through `to`. */
function inclusiveCalendarDays(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / DAY_MS) + 1
}

/**
 * Project this period's credit spend across the current month:
 * `spent ÷ elapsed days × days in the month`.
 *
 * Returns null when the window covers fewer than {@link MIN_PACE_ELAPSED_DAYS}
 * — there is not enough usage yet to say. `runOutAt` is filled in only when
 * enforcement is on and the balance is still positive. `now` is injectable.
 */
export function deriveSpendPace(
  spentCredits: number,
  periodFrom: string,
  periodTo: string,
  now: Date,
  enforcementEnabled: boolean,
  balanceCredits: number,
  projectedZeroAt: string | null,
): ISpendPace | null {
  if (!Number.isFinite(spentCredits)) return null
  const from = parseLocalYmd(periodFrom)
  const to = parseLocalYmd(periodTo)
  if (!from || !to) return null
  const elapsed = inclusiveCalendarDays(from, to)
  if (elapsed < MIN_PACE_ELAPSED_DAYS) return null
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const daysInMonth = monthEnd.getDate()
  const projectedCredits = Math.round((spentCredits / elapsed) * daysInMonth)
  const runOutAt =
    enforcementEnabled && balanceCredits > 0 ? (derivePace(projectedZeroAt, now)?.runOutAt ?? null) : null
  return { projectedCredits, monthEnd, runOutAt }
}

/** One meeting's credit usage, as the statement lists it, for the activity feed. */
export interface IUsageActivityRow {
  meetingId: string
  subject: string | null
  credits: number
  occurredAt?: string | null
}

/** One activity row: a ledger entry, or usage on a meeting. */
export type TActivityEntry =
  | { kind: "ledger"; at: string; transaction: ICreditTransactionView }
  | { kind: "usage"; at: string; usage: IUsageActivityRow }

function activityTime(iso: string | null | undefined): number {
  if (!iso) return Number.NEGATIVE_INFINITY
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY
}

/**
 * Merge ledger rows (grants, purchases, expiries, adjustments) with this
 * period's per-meeting usage, newest first. Usage of 0 credits is dropped —
 * it is not activity. Rows with no date sort last.
 */
export function mergeCreditActivity(
  transactions: readonly ICreditTransactionView[],
  usage: readonly IUsageActivityRow[],
): TActivityEntry[] {
  const entries: TActivityEntry[] = [
    ...transactions.map((transaction): TActivityEntry => ({ kind: "ledger", at: transaction.issuedAt, transaction })),
    ...usage
      .filter((row) => row.credits > 0)
      .map((row): TActivityEntry => ({ kind: "usage", at: row.occurredAt ?? "", usage: row })),
  ]
  entries.sort((a, b) => activityTime(b.at) - activityTime(a.at))
  return entries
}
